package com.artvending.warden

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.Draggable
import org.jetbrains.compose.web.css.CSSNumeric
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.paddingLeft
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.renderComposable


val space = 16.px
val bigSpace = space * 2
val smallSpace = space / 2


private fun StyleScope.setPaddings(paddings: Inset?, padding: CSSNumeric?, paddingHorizontal: CSSNumeric?, paddingVertical: CSSNumeric?, left: CSSNumeric?, top: CSSNumeric?, right: CSSNumeric?, bottom: CSSNumeric?, defaultValue: CSSNumeric? = null) {
	val left = left ?: paddingHorizontal ?: padding ?: paddings?.left ?: defaultValue
	val top = top ?: paddingVertical ?: padding ?: paddings?.top ?: defaultValue
	val right = right ?: paddingHorizontal ?: padding ?: paddings?.right ?: defaultValue
	val bottom = bottom ?: paddingVertical ?: padding ?: paddings?.bottom ?: defaultValue
	
	if (left != null)
		paddingLeft(left)
	
	if (top != null)
		paddingTop(top)
	
	if (right != null)
		paddingRight(right)
	
	if (bottom != null)
		paddingBottom(bottom)
}

private fun StyleScope.setMargins(margins: Inset?, margin: CSSNumeric?, marginHorizontal: CSSNumeric?, marginVertical: CSSNumeric?, left: CSSNumeric?, top: CSSNumeric?, right: CSSNumeric?, bottom: CSSNumeric?) {
	val left = left ?: marginHorizontal ?: margin ?: margins?.left
	val top = top ?: marginVertical ?: margin ?: margins?.top
	val right = right ?: marginHorizontal ?: margin ?: margins?.right
	val bottom = bottom ?: marginVertical ?: margin ?: margins?.bottom
	
	if (left != null)
		marginLeft(left)
	
	if (top != null)
		marginTop(top)
	
	if (right != null)
		marginRight(right)
	
	if (bottom != null)
		marginBottom(bottom)
}

private fun StyleScope.setAlignment(scope: ContainerScope, alignment: Alignment?, hAlignment: Alignment?, vAlignment: Alignment?) {
	val h = hAlignment ?: alignment
	val v = vAlignment ?: alignment
	
	when (scope) {
		is RowScope -> {
			if (v != null) {
				alignSelf(
					when (v) {
						Alignment.end -> "end"
						Alignment.center -> "center"
						Alignment.stretch -> "stretch"
						else -> "start"
					}
				)
			}
		}
		
		is ColumnScope -> {
			if (h != null)
				alignSelf(
					when (h) {
						Alignment.end -> "end"
						Alignment.center -> "center"
						Alignment.stretch -> "stretch"
						else -> "start"
					}
				)
		}

//		is BoxScope, is GridScope -> {
//			if (h != null)
//				justifySelf(
//					when (h) {
//						Alignment.end -> "end"
//						Alignment.center -> "center"
//						Alignment.stretch -> "stretch"
//						else -> "start"
//					}
//				)
//
//			if (v != null) {
//				alignSelf(
//					when (v) {
//						Alignment.end -> "end"
//						Alignment.center -> "center"
//						Alignment.stretch -> "stretch"
//						else -> "start"
//					}
//				)
//			}
//		}
		else -> {
			if (h != null)
				justifySelf(
					when (h) {
						Alignment.end -> "end"
						Alignment.center -> "center"
						Alignment.stretch -> "stretch"
						else -> "start"
					}
				)
			
			if (v != null) {
				alignSelf(
					when (v) {
						Alignment.end -> "end"
						Alignment.center -> "center"
						Alignment.stretch -> "stretch"
						else -> "start"
					}
				)
			}
		}
	}
}

private fun StyleScope.setBackgroundColor(value: CSSColorValue?) {
	if (value != null)
		backgroundColor(value)
}


private fun StyleScope.setSizes(size: CSSNumeric?, width: CSSNumeric?, height: CSSNumeric?) {
	val width = width ?: size
	val height = height ?: size
	
	if (width != null)
		width(width)
	
	if (height != null)
		height(height)
}


private fun StyleScope.setWeight(value: Number?) {
	if (value != null)
		flexGrow(value)
}


private fun StyleScope.setVisibility(value: Visibility?) {
	if (value != null)
		property(
			"visibility",
			when (value) {
				Visibility.visible -> "visible"
				Visibility.hidden -> "hidden"
				else -> "collapse"
			}
		)
}

private fun StyleScope.setTextColor(value: CSSColorValue?) {
	if (value != null)
		color(value)
}


private fun StyleScope.setGridArea(scope: ContainerScope) {
	if (scope is BoxScope)
		gridArea("1 / 1")
}


private fun AttrsScope<*>.setClasses(classes: String?) {
	if (classes != null)
		this.classes(classes.split(" ").filter { it != "" })
}


private fun StyleScope.setElevation(value: CSSNumeric?) {
	if (value != null)
		property("box-shadow", "0px $value ${value + 2.px} rgba(0, 0, 0, 0.1), 0px ${value / 4} ${(value + 2.px) / 1.5} rgba(0, 0, 0, 0.2)")
}


private fun StyleScope.setOverflow(value: Overflow?) {
	if (value != null)
		overflow(value.toString())
}


private fun StyleScope.setBorder(thickness: CSSLengthValue?, color: CSSColorValue?) {
	if (color == null) {
		if (thickness != null)
			border(thickness, LineStyle.Solid, color)
	}
	else
		border(1.px, LineStyle.Solid, color)
}


@Composable
private fun ContainerScope.DivContainer(
	vertical: Boolean?,
	size: CSSNumeric?,
	width: CSSNumeric?,
	height: CSSNumeric?,
	margins: Inset?,
	margin: CSSNumeric?,
	marginHorizontal: CSSNumeric?,
	marginVertical: CSSNumeric?,
	marginLeft: CSSNumeric?,
	marginTop: CSSNumeric?,
	marginRight: CSSNumeric?,
	marginBottom: CSSNumeric?,
	paddings: Inset?,
	padding: CSSNumeric?,
	paddingHorizontal: CSSNumeric?,
	paddingVertical: CSSNumeric?,
	paddingLeft: CSSNumeric?,
	paddingTop: CSSNumeric?,
	paddingRight: CSSNumeric?,
	paddingBottom: CSSNumeric?,
	space: CSSNumeric?,
	weight: Number?,
	alignment: Alignment?,
	hAlignment: Alignment?,
	vAlignment: Alignment?,
	backgroundColor: CSSColorValue?,
	textColor: CSSColorValue?,
	borderThickness: CSSLengthValue?,
	borderColor: CSSColorValue?,
	overflow: Overflow?,
	cornerRadius: CSSNumeric?,
	elevation: CSSNumeric?,
	visibility: Visibility?,
	opacity: Number?,
	onClick: (() -> Unit)? = null,
	classes: String? = null,
	style: (StyleScope.() -> Unit)?,
	content: @Composable ContainerScope.() -> Unit,
) {
	Div({
		setClasses(classes)
		
		style {
			if (vertical != null) {
				display(if (hAlignment == Alignment.start) DisplayStyle.LegacyInlineFlex else DisplayStyle.Flex)
				
				if (vertical)
					flexDirection(FlexDirection.Column)
			}
			
			setGridArea(this@DivContainer)
			setSizes(size, width, height)
			setMargins(margins, margin, marginHorizontal, marginVertical, marginLeft, marginTop, marginRight, marginBottom)
			setPaddings(paddings, padding, paddingHorizontal, paddingVertical, paddingLeft, paddingTop, paddingRight, paddingBottom)
			setAlignment(this@DivContainer, alignment, hAlignment, vAlignment)
			setBackgroundColor(backgroundColor)
			setTextColor(textColor)
			setWeight(weight)
			setVisibility(visibility)
			setOverflow(overflow)
			setBorder(borderThickness, borderColor)
			
			if (space != null)
				gap(space)
			
			if (cornerRadius != null)
				borderRadius(cornerRadius)
			
			if (opacity != null)
				opacity(opacity)
			
			setElevation(elevation)
			
			style?.invoke(this)
		}
		
		if (onClick != null)
			onClick { onClick() }
	}) {
		content(if (vertical == null) PanelScope() else if (vertical) ColumnScope() else RowScope())
	}
}


@Composable
fun ContainerScope.Column(
	size: CSSNumeric? = null,
	width: CSSNumeric? = null,
	height: CSSNumeric? = null,
	margins: Inset? = null,
	margin: CSSNumeric? = null,
	marginHorizontal: CSSNumeric? = null,
	marginVertical: CSSNumeric? = null,
	marginLeft: CSSNumeric? = null,
	marginTop: CSSNumeric? = null,
	marginRight: CSSNumeric? = null,
	marginBottom: CSSNumeric? = null,
	paddings: Inset? = null,
	padding: CSSNumeric? = null,
	paddingHorizontal: CSSNumeric? = null,
	paddingVertical: CSSNumeric? = null,
	paddingLeft: CSSNumeric? = null,
	paddingTop: CSSNumeric? = null,
	paddingRight: CSSNumeric? = null,
	paddingBottom: CSSNumeric? = null,
	space: CSSNumeric? = null,
	weight: Number? = null,
	alignment: Alignment? = null,
	hAlignment: Alignment? = null,
	vAlignment: Alignment? = null,
	backgroundColor: CSSColorValue? = null,
	textColor: CSSColorValue? = null,
	borderThickness: CSSLengthValue? = null,
	borderColor: CSSColorValue? = null,
	overflow: Overflow? = null,
	cornerRadius: CSSNumeric? = null,
	elevation: CSSNumeric? = null,
	opacity: Number? = null,
	visibility: Visibility? = null,
	onClick: (() -> Unit)? = null,
	classes: String? = null,
	style: (StyleScope.() -> Unit)? = null,
	content: @Composable ColumnScope.() -> Unit,
) {
	DivContainer(
		true,
		size = size,
		width = width,
		height = height,
		margins = margins,
		margin = margin,
		marginHorizontal = marginHorizontal,
		marginVertical = marginVertical,
		marginLeft = marginLeft,
		marginTop = marginTop,
		marginRight = marginRight,
		marginBottom = marginBottom,
		paddings = paddings,
		padding = padding,
		paddingHorizontal = paddingHorizontal,
		paddingVertical = paddingVertical,
		paddingLeft = paddingLeft,
		paddingTop = paddingTop,
		paddingRight = paddingRight,
		paddingBottom = paddingBottom,
		space = space,
		weight = weight,
		alignment = alignment,
		hAlignment = hAlignment,
		vAlignment = vAlignment,
		backgroundColor = backgroundColor,
		textColor = textColor,
		borderThickness = borderThickness,
		borderColor = borderColor,
		overflow = overflow,
		cornerRadius = cornerRadius,
		elevation = elevation,
		opacity = opacity,
		visibility = visibility,
		onClick = onClick,
		classes = classes,
		style = style,
		content = { content(ColumnScope()) }
	)
}


@Composable
fun ContainerScope.Row(
	size: CSSNumeric? = null,
	width: CSSNumeric? = null,
	height: CSSNumeric? = null,
	margins: Inset? = null,
	margin: CSSNumeric? = null,
	marginHorizontal: CSSNumeric? = null,
	marginVertical: CSSNumeric? = null,
	marginLeft: CSSNumeric? = null,
	marginTop: CSSNumeric? = null,
	marginRight: CSSNumeric? = null,
	marginBottom: CSSNumeric? = null,
	paddings: Inset? = null,
	padding: CSSNumeric? = null,
	paddingHorizontal: CSSNumeric? = null,
	paddingVertical: CSSNumeric? = null,
	paddingLeft: CSSNumeric? = null,
	paddingTop: CSSNumeric? = null,
	paddingRight: CSSNumeric? = null,
	paddingBottom: CSSNumeric? = null,
	space: CSSNumeric? = null,
	weight: Number? = null,
	alignment: Alignment? = null,
	hAlignment: Alignment? = null,
	vAlignment: Alignment? = null,
	backgroundColor: CSSColorValue? = null,
	textColor: CSSColorValue? = null,
	borderThickness: CSSLengthValue? = null,
	borderColor: CSSColorValue? = null,
	overflow: Overflow? = null,
	cornerRadius: CSSNumeric? = null,
	elevation: CSSNumeric? = null,
	opacity: Number? = null,
	visibility: Visibility? = null,
	onClick: (() -> Unit)? = null,
	classes: String? = null,
	style: (StyleScope.() -> Unit)? = null,
	content: @Composable RowScope.() -> Unit,
) {
	DivContainer(
		false,
		size = size,
		width = width,
		height = height,
		margins = margins,
		margin = margin,
		marginHorizontal = marginHorizontal,
		marginVertical = marginVertical,
		marginLeft = marginLeft,
		marginTop = marginTop,
		marginRight = marginRight,
		marginBottom = marginBottom,
		paddings = paddings,
		padding = padding,
		paddingHorizontal = paddingHorizontal,
		paddingVertical = paddingVertical,
		paddingLeft = paddingLeft,
		paddingTop = paddingTop,
		paddingRight = paddingRight,
		paddingBottom = paddingBottom,
		space = space,
		weight = weight,
		alignment = alignment,
		hAlignment = hAlignment,
		vAlignment = vAlignment,
		backgroundColor = backgroundColor,
		textColor = textColor,
		borderThickness = borderThickness,
		borderColor = borderColor,
		overflow = overflow,
		cornerRadius = cornerRadius,
		elevation = elevation,
		opacity = opacity,
		visibility = visibility,
		onClick = onClick,
		classes = classes,
		style = style,
		content = { content(RowScope()) }
	)
}


@Composable
fun ContainerScope.Panel(
	size: CSSNumeric? = null,
	width: CSSNumeric? = null,
	height: CSSNumeric? = null,
	margins: Inset? = null,
	margin: CSSNumeric? = null,
	marginHorizontal: CSSNumeric? = null,
	marginVertical: CSSNumeric? = null,
	marginLeft: CSSNumeric? = null,
	marginTop: CSSNumeric? = null,
	marginRight: CSSNumeric? = null,
	marginBottom: CSSNumeric? = null,
	paddings: Inset? = null,
	padding: CSSNumeric? = null,
	paddingHorizontal: CSSNumeric? = null,
	paddingVertical: CSSNumeric? = null,
	paddingLeft: CSSNumeric? = null,
	paddingTop: CSSNumeric? = null,
	paddingRight: CSSNumeric? = null,
	paddingBottom: CSSNumeric? = null,
	space: CSSNumeric? = null,
	weight: Number? = null,
	alignment: Alignment? = null,
	hAlignment: Alignment? = null,
	vAlignment: Alignment? = null,
	backgroundColor: CSSColorValue? = null,
	textColor: CSSColorValue? = null,
	borderThickness: CSSLengthValue? = null,
	borderColor: CSSColorValue? = null,
	overflow: Overflow? = null,
	cornerRadius: CSSNumeric? = null,
	elevation: CSSNumeric? = null,
	opacity: Number? = null,
	visibility: Visibility? = null,
	onClick: (() -> Unit)? = null,
	classes: String? = null,
	style: (StyleScope.() -> Unit)? = null,
	content: @Composable ColumnScope.() -> Unit,
) {
	DivContainer(
		null,
		size = size,
		width = width,
		height = height,
		margins = margins,
		margin = margin,
		marginHorizontal = marginHorizontal,
		marginVertical = marginVertical,
		marginLeft = marginLeft,
		marginTop = marginTop,
		marginRight = marginRight,
		marginBottom = marginBottom,
		paddings = paddings,
		padding = padding,
		paddingHorizontal = paddingHorizontal,
		paddingVertical = paddingVertical,
		paddingLeft = paddingLeft,
		paddingTop = paddingTop,
		paddingRight = paddingRight,
		paddingBottom = paddingBottom,
		space = space,
		weight = weight,
		alignment = alignment,
		hAlignment = hAlignment,
		vAlignment = vAlignment,
		backgroundColor = backgroundColor,
		textColor = textColor,
		borderThickness = borderThickness,
		borderColor = borderColor,
		overflow = overflow,
		cornerRadius = cornerRadius,
		elevation = elevation,
		opacity = opacity,
		visibility = visibility,
		onClick = onClick,
		classes = classes,
		style = style,
		content = { content(ColumnScope()) }
	)
}


@Composable
fun ContainerScope.Box(
	size: CSSNumeric? = null,
	width: CSSNumeric? = null,
	height: CSSNumeric? = null,
	margins: Inset? = null,
	margin: CSSNumeric? = null,
	marginHorizontal: CSSNumeric? = null,
	marginVertical: CSSNumeric? = null,
	marginLeft: CSSNumeric? = null,
	marginTop: CSSNumeric? = null,
	marginRight: CSSNumeric? = null,
	marginBottom: CSSNumeric? = null,
	paddings: Inset? = null,
	padding: CSSNumeric? = null,
	paddingHorizontal: CSSNumeric? = null,
	paddingVertical: CSSNumeric? = null,
	paddingLeft: CSSNumeric? = null,
	paddingTop: CSSNumeric? = null,
	paddingRight: CSSNumeric? = null,
	paddingBottom: CSSNumeric? = null,
	weight: Number? = null,
	alignment: Alignment? = null,
	hAlignment: Alignment? = null,
	vAlignment: Alignment? = null,
	backgroundColor: CSSColorValue? = null,
	textColor: CSSColorValue? = null,
	borderThickness: CSSLengthValue? = null,
	borderColor: CSSColorValue? = null,
	overflow: Overflow? = null,
	cornerRadius: CSSNumeric? = null,
	elevation: CSSNumeric? = null,
	opacity: Number? = null,
	visibility: Visibility? = null,
	onClick: (() -> Unit)? = null,
	classes: String? = null,
	style: (StyleScope.() -> Unit)? = null,
	content: @Composable BoxScope.() -> Unit,
) {
	Div({
		setClasses(classes)
		
		style {
			display(if (hAlignment == Alignment.start) DisplayStyle.LegacyInlineGrid else DisplayStyle.Grid)
			setGridArea(this@Box)
			setSizes(size, width, height)
			setMargins(margins, margin, marginHorizontal, marginVertical, marginLeft, marginTop, marginRight, marginBottom)
			setPaddings(paddings, padding, paddingHorizontal, paddingVertical, paddingLeft, paddingTop, paddingRight, paddingBottom)

//			setAlignment(
//				this@Box,
//				if (hAlignment == null) Alignment.start else hAlignment,
//				if (vAlignment == null) Alignment.start else vAlignment,
//			)
			
			setAlignment(this@Box, alignment, hAlignment, vAlignment)
			setBackgroundColor(backgroundColor)
			setTextColor(textColor)
			setWeight(weight)
			setVisibility(visibility)
			setOverflow(overflow)
			setBorder(borderThickness, borderColor)
			
			if (cornerRadius != null)
				borderRadius(cornerRadius)
			
			if (opacity != null)
				opacity(opacity)
			
			setElevation(elevation)
			
			style?.invoke(this)
		}
		
		if (onClick != null)
			onClick { onClick() }
	}) {
		content(BoxScope())
	}
}


@Composable
fun ContainerScope.Grid(
	columns: List<GridCellSize>? = null,
	rows: List<GridCellSize>? = null,
	size: CSSNumeric? = null,
	width: CSSNumeric? = null,
	height: CSSNumeric? = null,
	margins: Inset? = null,
	margin: CSSNumeric? = null,
	marginHorizontal: CSSNumeric? = null,
	marginVertical: CSSNumeric? = null,
	marginLeft: CSSNumeric? = null,
	marginTop: CSSNumeric? = null,
	marginRight: CSSNumeric? = null,
	marginBottom: CSSNumeric? = null,
	paddings: Inset? = null,
	padding: CSSNumeric? = null,
	paddingHorizontal: CSSNumeric? = null,
	paddingVertical: CSSNumeric? = null,
	paddingLeft: CSSNumeric? = null,
	paddingTop: CSSNumeric? = null,
	paddingRight: CSSNumeric? = null,
	paddingBottom: CSSNumeric? = null,
	space: CSSNumeric? = null,
	weight: Number? = null,
	alignment: Alignment? = null,
	hAlignment: Alignment? = null,
	vAlignment: Alignment? = null,
	backgroundColor: CSSColorValue? = null,
	textColor: CSSColorValue? = null,
	borderThickness: CSSLengthValue? = null,
	borderColor: CSSColorValue? = null,
	overflow: Overflow? = null,
	cornerRadius: CSSNumeric? = null,
	elevation: CSSNumeric? = null,
	opacity: Number? = null,
	visibility: Visibility? = null,
	onClick: (() -> Unit)? = null,
	classes: String? = null,
	style: (StyleScope.() -> Unit)? = null,
	content: @Composable GridScope.() -> Unit,
) {
	Div({
		setClasses(classes)
		
		style {
			display(if (hAlignment == Alignment.start) DisplayStyle.LegacyInlineGrid else DisplayStyle.Grid)
			setGridArea(this@Grid)
			
			println(columns?.joinToString(" "))
			
			if (columns?.isNotEmpty() == true) {
				gridTemplateColumns(columns.joinToString(" "))
			}
			
			if (rows?.isNotEmpty() == true)
				gridTemplateRows(rows.joinToString(" "))
			
			if (space != null)
				gap(space)
			
			setSizes(size, width, height)
			setMargins(margins, margin, marginHorizontal, marginVertical, marginLeft, marginTop, marginRight, marginBottom)
			setPaddings(paddings, padding, paddingHorizontal, paddingVertical, paddingLeft, paddingTop, paddingRight, paddingBottom)

//			setAlignment(
//				this@Box,
//				if (hAlignment == null) Alignment.start else hAlignment,
//				if (vAlignment == null) Alignment.start else vAlignment,
//			)
			
			setAlignment(this@Grid, alignment, hAlignment, vAlignment)
			setBackgroundColor(backgroundColor)
			setTextColor(textColor)
			setWeight(weight)
			setVisibility(visibility)
			setOverflow(overflow)
			setBorder(borderThickness, borderColor)
			
			if (cornerRadius != null)
				borderRadius(cornerRadius)
			
			if (opacity != null)
				opacity(opacity)
			
			setElevation(elevation)
			
			style?.invoke(this)
		}
		
		if (onClick != null)
			onClick { onClick() }
	}) {
		content(GridScope())
	}
}


@Composable
fun ContainerScope.Scaffold(
	size: CSSNumeric? = null,
	width: CSSNumeric? = null,
	height: CSSNumeric? = null,
	margins: Inset? = null,
	margin: CSSNumeric? = null,
	marginHorizontal: CSSNumeric? = null,
	marginVertical: CSSNumeric? = null,
	marginLeft: CSSNumeric? = null,
	marginTop: CSSNumeric? = null,
	marginRight: CSSNumeric? = null,
	marginBottom: CSSNumeric? = null,
	paddings: Inset? = null,
	padding: CSSNumeric? = null,
	paddingHorizontal: CSSNumeric? = null,
	paddingVertical: CSSNumeric? = null,
	paddingLeft: CSSNumeric? = null,
	paddingTop: CSSNumeric? = null,
	paddingRight: CSSNumeric? = null,
	paddingBottom: CSSNumeric? = null,
	space: CSSNumeric? = null,
	weight: Number? = null,
	alignment: Alignment? = null,
	hAlignment: Alignment? = null,
	vAlignment: Alignment? = null,
	backgroundColor: CSSColorValue? = null,
	textColor: CSSColorValue? = null,
	borderThickness: CSSLengthValue? = null,
	borderColor: CSSColorValue? = null,
	overflow: Overflow? = null,
	cornerRadius: CSSNumeric? = null,
	elevation: CSSNumeric? = null,
	opacity: Number? = null,
	visibility: Visibility? = null,
	onClick: (() -> Unit)? = null,
	classes: String? = null,
	style: (StyleScope.() -> Unit)? = null,
	content: @Composable ScaffoldScope.() -> Unit,
) {
	val scope = ScaffoldScope()
	scope.content()
	
	Column(
		size = size,
		width = width,
		height = height,
		margins = margins,
		margin = margin,
		marginHorizontal = marginHorizontal,
		marginVertical = marginVertical,
		marginLeft = marginLeft,
		marginTop = marginTop,
		marginRight = marginRight,
		marginBottom = marginBottom,
		paddings = paddings,
		padding = padding,
		paddingHorizontal = paddingHorizontal,
		paddingVertical = paddingVertical,
		paddingLeft = paddingLeft,
		paddingTop = paddingTop,
		paddingRight = paddingRight,
		paddingBottom = paddingBottom,
		space = space,
		weight = weight,
		alignment = alignment,
		hAlignment = hAlignment,
		vAlignment = vAlignment,
		backgroundColor = backgroundColor,
		textColor = textColor,
		borderThickness = borderThickness,
		borderColor = borderColor,
		overflow = overflow,
		cornerRadius = cornerRadius,
		elevation = elevation,
		opacity = opacity,
		visibility = visibility,
		onClick = onClick,
		classes = classes,
		style = style,
	) {
		scope.topContent?.invoke(this)
		Row(weight = 1, space = space) {
			scope.leftContent?.invoke(this)
			Box(weight = 1) {
				scope.centerContent?.invoke(this)
			}
			scope.rightContent?.invoke(this)
		}
		scope.bottomContent?.invoke(this)
	}

//	Div({
//		setClasses(classes)
//
//		style {
//			display(if (hAlignment == Alignment.start) DisplayStyle.LegacyInlineGrid else DisplayStyle.Grid)
//			setGridArea(this@Scaffold)
//			setSizes(size, width, height)
//			setMargins(margins, margin, marginHorizontal, marginVertical, marginLeft, marginRight, marginTop, marginBottom)
//			setPaddings(paddings, padding, paddingHorizontal, paddingVertical, paddingLeft, paddingTop, paddingRight, paddingBottom)
//			setAlignment(this@Scaffold, alignment, hAlignment, vAlignment)
//			setBackgroundColor(backgroundColor)
//			setTextColor(textColor)
//			setWeight(weight)
//			setVisibility(visibility)
//
//			if (cornerRadius != null)
//				borderRadius(cornerRadius)
//
//			setElevation(elevation)
//
//			style?.invoke(this)
//		}
//
//		if (onClick != null)
//			onClick { onClick() }
//	}) {
//		val scope = ScaffoldScope()
//		scope.content()
//
//		Column(space = space) {
//			scope.topContent?.invoke(this)
//			Row(weight = 1, space = space) {
//				scope.leftContent?.invoke(this)
//				Box(weight = 1) {
//					scope.centerContent?.invoke(this)
//				}
//				scope.rightContent?.invoke(this)
//			}
//			scope.bottomContent?.invoke(this)
//		}
//	}
}


@Composable
fun ContainerScope.Text_(
	text: String,
	size: CSSNumeric? = null,
	width: CSSNumeric? = null,
	height: CSSNumeric? = null,
	margins: Inset? = null,
	margin: CSSNumeric? = null,
	marginHorizontal: CSSNumeric? = null,
	marginVertical: CSSNumeric? = null,
	marginLeft: CSSNumeric? = null,
	marginTop: CSSNumeric? = null,
	marginRight: CSSNumeric? = null,
	marginBottom: CSSNumeric? = null,
	paddings: Inset? = null,
	padding: CSSNumeric? = null,
	paddingHorizontal: CSSNumeric? = null,
	paddingVertical: CSSNumeric? = null,
	paddingLeft: CSSNumeric? = null,
	paddingTop: CSSNumeric? = null,
	paddingRight: CSSNumeric? = null,
	paddingBottom: CSSNumeric? = null,
	textAlignment: TextAlignment? = null,
	fontWeight: Int? = null,
	weight: Number? = null,
	alignment: Alignment? = null,
	hAlignment: Alignment? = null,
	vAlignment: Alignment? = null,
	backgroundColor: CSSColorValue? = null,
	color: CSSColorValue? = null,
	borderThickness: CSSLengthValue? = null,
	borderColor: CSSColorValue? = null,
	cornerRadius: CSSNumeric? = null,
	opacity: Number? = null,
	visibility: Visibility? = null,
	columnIndex: Int? = null,
	columnSpan: Int? = null,
	rowIndex: Int? = null,
	rowSpan: Int? = null,
	onClick: (() -> Unit)? = null,
	classes: String? = null,
	style: (StyleScope.() -> Unit)? = null,
) {
	Span({
		setClasses(classes)
		
		style {
			display(DisplayStyle.InlineBlock)
			setGridArea(this@Text_)
			setSizes(size, width, height)
			setMargins(margins, margin, marginHorizontal, marginVertical, marginLeft, marginTop, marginRight, marginBottom)
			setPaddings(paddings, padding, paddingHorizontal, paddingVertical, paddingLeft, paddingTop, paddingRight, paddingBottom)
			setAlignment(this@Text_, alignment, hAlignment, vAlignment)
			setBackgroundColor(backgroundColor)
			setTextColor(color)
			setWeight(weight)
			setVisibility(visibility)
			setBorder(borderThickness, borderColor)
			
			if (textAlignment != null) {
				textAlign(
					when (textAlignment) {
						TextAlignment.start -> "start"
						TextAlignment.end -> "end"
						TextAlignment.center -> "center"
						TextAlignment.justify -> "justify"
					}
				)
			}
			
			if (this@Text_ is GridScope) {
				if (columnIndex != null) {
					gridColumnStart(columnIndex + 1)
					gridColumnEnd(columnIndex + 1 + (columnSpan ?: 0))
				}
				
				if (rowIndex != null) {
					gridColumnStart(rowIndex + 1)
					gridColumnEnd(rowIndex + 1 + (rowSpan ?: 0))
				}
			}
			
			if (fontWeight != null)
				fontWeight(fontWeight)
			
			if (opacity != null)
				opacity(opacity)
			
			if (cornerRadius != null)
				borderRadius(cornerRadius)
			
			style?.invoke(this)
		}
		
		if (onClick != null)
			onClick { onClick() }
	}) {
		org.jetbrains.compose.web.dom.Text(text)
	}
}


@Composable
fun ContainerScope.Image(
	source: String,
	alt: String? = null,
	size: CSSNumeric? = null,
	width: CSSNumeric? = null,
	height: CSSNumeric? = null,
	margins: Inset? = null,
	margin: CSSNumeric? = null,
	marginHorizontal: CSSNumeric? = null,
	marginVertical: CSSNumeric? = null,
	marginLeft: CSSNumeric? = null,
	marginTop: CSSNumeric? = null,
	marginRight: CSSNumeric? = null,
	marginBottom: CSSNumeric? = null,
	paddings: Inset? = null,
	padding: CSSNumeric? = null,
	paddingHorizontal: CSSNumeric? = null,
	paddingVertical: CSSNumeric? = null,
	paddingLeft: CSSNumeric? = null,
	paddingTop: CSSNumeric? = null,
	paddingRight: CSSNumeric? = null,
	paddingBottom: CSSNumeric? = null,
	weight: Number? = null,
	alignment: Alignment? = null,
	hAlignment: Alignment? = null,
	vAlignment: Alignment? = null,
	backgroundColor: CSSColorValue? = null,
	borderThickness: CSSLengthValue? = null,
	borderColor: CSSColorValue? = null,
	cornerRadius: CSSNumeric? = null,
	elevation: CSSNumeric? = null,
	opacity: Number? = null,
	visibility: Visibility? = null,
	columnIndex: Int? = null,
	columnSpan: Int? = null,
	rowIndex: Int? = null,
	rowSpan: Int? = null,
	onClick: (() -> Unit)? = null,
	classes: String? = null,
	style: (StyleScope.() -> Unit)? = null,
) {
	org.jetbrains.compose.web.dom.Img(source, alt ?: "", {
		setClasses(classes)
		
		style {
			setGridArea(this@Image)
			setSizes(size, width, height)
			setMargins(margins, margin, marginHorizontal, marginVertical, marginLeft, marginTop, marginRight, marginBottom)
			setPaddings(paddings, padding, paddingHorizontal, paddingVertical, paddingLeft, paddingTop, paddingRight, paddingBottom)
			setAlignment(this@Image, alignment, hAlignment, vAlignment)
			setBackgroundColor(backgroundColor)
			setWeight(weight)
			setVisibility(visibility)
			setElevation(elevation)
			setBorder(borderThickness, borderColor)
			
			if (cornerRadius != null)
				borderRadius(cornerRadius)
			
			if (opacity != null)
				opacity(opacity)
			
			if (this@Image is GridScope) {
				if (columnIndex != null) {
					gridColumnStart(columnIndex + 1)
					gridColumnEnd(columnIndex + 1 + (columnSpan ?: 0))
				}
				
				if (rowIndex != null) {
					gridColumnStart(rowIndex + 1)
					gridColumnEnd(rowIndex + 1 + (rowSpan ?: 0))
				}
			}
			
			style?.invoke(this)
		}
		
		draggable(Draggable.False)
		
		if (onClick != null)
			onClick { onClick() }
	})
}


@Composable
fun StackScope.Space(
	size: CSSNumeric? = null,
	weight: Number? = null,
	style: (StyleScope.() -> Unit)? = null,
) {
	Div({
		style {
			if (size != null)
				if (this@Space is RowScope)
					width(size)
				else
					height(size)
			
			setWeight(weight)
			style?.invoke(this)
		}
	})
}


@Composable
fun ContainerScope.Button(
	text: String? = null,
	image: String? = null,
	imageSize: CSSNumeric? = null,
	imageWidth: CSSNumeric? = null,
	imageHeight: CSSNumeric? = null,
	imagePlace: RelativePlace? = null,
	stretchContent: Boolean? = null,
	size: CSSNumeric? = null,
	width: CSSNumeric? = null,
	height: CSSNumeric? = null,
	margins: Inset? = null,
	margin: CSSNumeric? = null,
	marginHorizontal: CSSNumeric? = null,
	marginVertical: CSSNumeric? = null,
	marginLeft: CSSNumeric? = null,
	marginTop: CSSNumeric? = null,
	marginRight: CSSNumeric? = null,
	marginBottom: CSSNumeric? = null,
	padding: CSSNumeric? = null,
	cornerRadius: CSSNumeric? = null,
	weight: Number? = null,
	alignment: Alignment? = null,
	hAlignment: Alignment? = null,
	vAlignment: Alignment? = null,
	textColor: CSSColorValue? = null,
	opacity: Number? = null,
	visibility: Visibility? = null,
	columnIndex: Int? = null,
	columnSpan: Int? = null,
	rowIndex: Int? = null,
	rowSpan: Int? = null,
	style: (StyleScope.() -> Unit)? = null,
	classes: String? = null,
	clickByTouch: Boolean? = null,
	onClick: () -> Unit,
) {
	val _this = this
	
	org.jetbrains.compose.web.dom.Button({
		setClasses(classes)
		
		if (clickByTouch == true)
			addEventListener("touchstart") {
				onClick()
			}
		else
			onClick { onClick() }
		
		style {
			if (stretchContent != true) {
				display(DisplayStyle.Flex)
				justifyContent(JustifyContent.Center)
				alignItems(AlignItems.Center)
			}
			
			if (cornerRadius != null)
				borderRadius(cornerRadius)
			
			setGridArea(_this)
			setSizes(size, width, height)
			setMargins(margins, margin, marginHorizontal, marginVertical, marginLeft, marginTop, marginRight, marginBottom)
			setAlignment(_this, alignment, hAlignment, vAlignment)
			setTextColor(textColor)
			setWeight(weight)
			setVisibility(visibility)
			padding(padding ?: smallSpace)
			
			if (opacity != null)
				opacity(opacity)
			
			if (_this is GridScope) {
				if (columnIndex != null) {
					gridColumnStart(columnIndex + 1)
					gridColumnEnd(columnIndex + 1 + (columnSpan ?: 0))
				}
				
				if (rowIndex != null) {
					gridColumnStart(rowIndex + 1)
					gridColumnEnd(rowIndex + 1 + (rowSpan ?: 0))
				}
			}
			
			style?.invoke(this)
		}
	}) {
		if (image != null) {
			@Composable
			fun ContainerScope.Img() {
				Image(image, size = imageSize, width = imageWidth, height = imageHeight, alignment = Alignment.center)
			}
			
			if (text == null || text == "")
				Img()
			else
				if (stretchContent == true)
					Scaffold(space = padding ?: smallSpace) {
						when (imagePlace) {
							null, RelativePlace.left -> {
								left { Img() }
							}
							
							RelativePlace.right -> {
								right { Img() }
							}
							
							RelativePlace.top -> {
								top { Img() }
							}
							
							RelativePlace.bottom -> {
								bottom { Img() }
							}
						}
						center {
							Text_(text, alignment = Alignment.center)
						}
					}
				else {
					when (imagePlace) {
						null, RelativePlace.left -> {
							Row(space = padding ?: smallSpace, alignment = Alignment.center) {
								Img()
								Text_(text, alignment = Alignment.center)
							}
						}
						
						RelativePlace.right -> {
							Row(space = padding ?: smallSpace, alignment = Alignment.center) {
								Text_(text, alignment = Alignment.center)
								Img()
							}
						}
						
						RelativePlace.top -> {
							Column(space = padding ?: smallSpace, alignment = Alignment.center) {
								Img()
								Text_(text, alignment = Alignment.center)
							}
						}
						
						RelativePlace.bottom -> {
							Column(space = padding ?: smallSpace, alignment = Alignment.center) {
								Text_(text, alignment = Alignment.center)
								Img()
							}
						}
					}
				}
		}
		else
			if (text != null)
				Text_(text)
	}
}


@Composable
fun ContainerScope.Rectangle(
	size: CSSNumeric? = null,
	width: CSSNumeric? = null,
	height: CSSNumeric? = null,
	margins: Inset? = null,
	margin: CSSNumeric? = null,
	marginHorizontal: CSSNumeric? = null,
	marginVertical: CSSNumeric? = null,
	marginLeft: CSSNumeric? = null,
	marginTop: CSSNumeric? = null,
	marginRight: CSSNumeric? = null,
	marginBottom: CSSNumeric? = null,
	weight: Number? = null,
	alignment: Alignment? = null,
	hAlignment: Alignment? = null,
	vAlignment: Alignment? = null,
	backgroundColor: CSSColorValue? = null,
	borderThickness: CSSLengthValue? = null,
	borderColor: CSSColorValue? = null,
	cornerRadius: CSSNumeric? = null,
	elevation: CSSNumeric? = null,
	opacity: Number? = null,
	visibility: Visibility? = null,
	columnIndex: Int? = null,
	columnSpan: Int? = null,
	rowIndex: Int? = null,
	rowSpan: Int? = null,
	onClick: (() -> Unit)? = null,
	classes: String? = null,
	style: (StyleScope.() -> Unit)? = null,
) {
	//val _this = this
	
	Div({
		setClasses(classes)
		
		style {
			setGridArea(this@Rectangle)
			setSizes(size, width, height)
			setMargins(margins, margin, marginHorizontal, marginVertical, marginLeft, marginTop, marginRight, marginBottom)
			setAlignment(this@Rectangle, alignment, hAlignment, vAlignment)
			setBackgroundColor(backgroundColor)
			setWeight(weight)
			setVisibility(visibility)
			setBorder(borderThickness, borderColor)
			
			if (cornerRadius != null)
				borderRadius(cornerRadius)
			
			if (opacity != null)
				opacity(opacity)
			
			setElevation(elevation)
			
			if (this@Rectangle is GridScope) {
				if (columnIndex != null) {
					gridColumnStart(columnIndex + 1)
					gridColumnEnd(columnIndex + 1 + (columnSpan ?: 0))
				}
				
				if (rowIndex != null) {
					gridColumnStart(rowIndex + 1)
					gridColumnEnd(rowIndex + 1 + (rowSpan ?: 0))
				}
			}
			
			style?.invoke(this)
		}
		
		if (onClick != null)
			onClick { onClick() }
	})
}


class ScaffoldScope() {
	var topContent: (@Composable ColumnScope.() -> Unit)? = null
	var leftContent: (@Composable RowScope.() -> Unit)? = null
	var centerContent: (@Composable BoxScope.() -> Unit)? = null
	var rightContent: (@Composable RowScope.() -> Unit)? = null
	var bottomContent: (@Composable ColumnScope.() -> Unit)? = null
	
	fun top(content: @Composable ColumnScope.() -> Unit) {
		topContent = content
	}
	
	fun left(content: @Composable RowScope.() -> Unit) {
		leftContent = content
	}
	
	fun center(content: @Composable BoxScope.() -> Unit) {
		centerContent = content
	}
	
	fun right(content: @Composable RowScope.() -> Unit) {
		rightContent = content
	}
	
	fun bottom(content: @Composable ColumnScope.() -> Unit) {
		bottomContent = content
	}
}

interface GridCellSize {
	//fun toString(): String
}

class GridCellFixedSize(val size: CSSNumeric) : GridCellSize {
	override fun toString() = size.toString()
}

class GridCellFreeSize(val weight: Number = 1) : GridCellSize {
	override fun toString() = "${weight}fr"
}

class GridCellAdaptiveSize : GridCellSize {
	override fun toString() = "auto"
}


class Inset(val left: CSSNumeric, val top: CSSNumeric, val right: CSSNumeric, val bottom: CSSNumeric)


enum class Alignment {
	start, end, center, stretch
}

enum class Visibility {
	visible, hidden, collapsed
}

enum class TextAlignment {
	start, end, center, justify
}

class Sizes(val width: CSSNumeric?, val height: CSSNumeric?)


interface ContainerScope

interface StackScope : ContainerScope

class ColumnScope : StackScope

class RowScope : StackScope

class BoxScope : ContainerScope

class GridScope : ContainerScope

class PanelScope: ContainerScope


fun root(block: @Composable BoxScope.() -> Unit) {
	renderComposable(rootElementId = "root") {
		block(BoxScope())
	}
}


@Composable
fun ContainerScope.native(block: @Composable () -> Unit) {
	block()
}


enum class RelativePlace {
	left, top, right, bottom
}


enum class Overflow(val cssValue: String) {
	hidden("hidden"), visible("visible"), autoScroll("auto");
	
	override fun toString() = cssValue
}

object FontWeight {
	const val normal = 400
	const val bold = 700
}